/**
 * FiasAIContext — COMPATIBILITY SHIM over the global Fias AI window.
 *
 * Historically each arche mounted its own FiasAIProvider + popup with an
 * isolated chat. There is now ONE global assistant (GlobalFiasAIProvider in
 * App.tsx); this provider survives so existing arche call sites keep
 * compiling: it registers the arche's capabilities with the global registry
 * (useRegisterArcheAI) and delegates panel/chat controls to the global
 * window. Per-arche popups are gone — `togglePanel` opens the global
 * window, pre-scoped to this arche.
 *
 * New arche pages should call useRegisterArcheAI directly instead of
 * wrapping in this provider.
 */

import { createContext, useContext } from 'react';
import type { FiasAIChatMessage } from '../types';
import type { ArcheAICapabilities } from '../types';
import { useGlobalFiasAIOptional } from './GlobalFiasAIContext';
import { useRegisterArcheAI } from './useRegisterArcheAI';

interface FiasAIContextValue {
  capabilities: ArcheAICapabilities | null;
  isPanelOpen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  messages: FiasAIChatMessage[];
  isStreaming: boolean;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  submit: (message: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  applyProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
}

const FiasAIContext = createContext<FiasAIContextValue | null>(null);

export function useFiasAIContext(): FiasAIContextValue {
  const context = useContext(FiasAIContext);
  if (!context) {
    throw new Error('useFiasAIContext must be used within a FiasAIProvider');
  }
  return context;
}

interface FiasAIProviderProps {
  capabilities: ArcheAICapabilities;
  children: React.ReactNode;
}

const noop = () => undefined;

/**
 * Registers the arche's capabilities with the global assistant and exposes
 * the legacy context shape, delegating to the global window.
 */
export function FiasAIProvider({ capabilities, children }: FiasAIProviderProps) {
  useRegisterArcheAI(capabilities.enabled === false ? null : capabilities);
  const global = useGlobalFiasAIOptional();

  const value: FiasAIContextValue = {
    capabilities,
    isPanelOpen: global?.isPanelOpen ?? false,
    togglePanel: global?.togglePanel ?? noop,
    openPanel: global?.openPanel ?? noop,
    closePanel: global?.closePanel ?? noop,
    messages: global?.messages ?? [],
    isStreaming: global?.isStreaming ?? false,
    currentModel: global?.currentModel ?? null,
    submit: global?.submit ?? (async () => undefined),
    stop: global?.stop ?? noop,
    reset: global?.reset ?? noop,
    // Agent-mode actions auto-apply / confirm through the global window;
    // the legacy manual-proposal controls are inert.
    applyProposal: noop,
    rejectProposal: noop,
  };

  return <FiasAIContext.Provider value={value}>{children}</FiasAIContext.Provider>;
}
